import { components as c } from "@/components";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { SubmitHandler, useForm } from "react-hook-form";
import { useDiaryByIdQuery } from "@/features/diaries";
import { useShockwaveMutation } from "@/features/shockwave/api/upsertShockwave";
import useExtractShockwave from "@/features/shockwave/hooks/useExtractShockwave";
import { ui } from "@/components/ui";
import { useAuth } from "@/utils/auth";
import Stepper from "@/components/common/Stepper";
import { z } from "zod";
import {
	useEventStepper,
} from "@/features/templates/hooks/useEventNavigationStore";
import ShockwaveFormFields from "@/features/shockwave/components/ShockwaveFormFields";

const shockwaveSchemaZ = z.object({
	event_id: z.string().uuid(),
	shockwave_id: z.string().uuid(),
	referring_clinician_id: z.string().uuid().optional(),
	shockwaving_clinician_id: z.string().uuid().optional(),
});

type ShockwaveSchemaT = z.infer<typeof shockwaveSchemaZ>;

const ShockwaveFormFieldsPage = () => {
	const { diaryId, eventId, shockwaveId } = Route.useParams();
	const auth = useAuth();
	const shockwave = useExtractShockwave(diaryId, shockwaveId);
	const { data: diary } = useDiaryByIdQuery(diaryId);

	const navigate = useNavigate();

	const shockwaveMutation = useShockwaveMutation({ diaryId: diaryId });

	const form = useForm<ShockwaveSchemaT>({
		resolver: zodResolver(shockwaveSchemaZ),
		defaultValues: {
			event_id: eventId,
			shockwave_id: shockwaveId,
			referring_clinician_id: shockwave?.referring_clinician_id ??
				auth.id,
			shockwaving_clinician_id: shockwave?.shockwaving_clinician_id ??
				auth.id,
		},
	});

	const onSuccess = () => {
		navigate({
			to: "/new/diary/$diaryId/events/$eventId/shockwave/$shockwaveId/details",
			params: { diaryId, eventId, shockwaveId },
		});
	};

	const onSubmit: SubmitHandler<ShockwaveSchemaT> = async (formData) => {
		console.log(formData);
		shockwaveMutation.mutate(formData, { onSuccess: onSuccess });
	};

	const handlePrevious = () => {
		navigate({
			to: "/new/diary/$diaryId/events/$eventId/details",
			params: { diaryId, eventId },
		});
	};

	const { steps } = useEventStepper("shockwave", "shockwave");

	return (
		<div className="space-y-8">
			<Stepper steps={steps} />
			<ui.Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} className="form">
					<ShockwaveFormFields
						organisationId={diary?.organisation_id as string}
					/>
					<div className="flex justify-between gap-4 mt-8">
						<c.ui.Button
							type="button"
							variant="outline"
							onClick={handlePrevious}
						>
							Previous
						</c.ui.Button>
						<c.fc.NextButton />
					</div>
				</form>
			</ui.Form>
		</div>
	);
};

export const Route = createFileRoute(
	"/_app/new/diary/$diaryId/events/$eventId/shockwave/$shockwaveId/",
)({
	component: ShockwaveFormFieldsPage,
});
